import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Volume2, BookOpen, Trophy, Flame } from 'lucide-react';
import { toast } from 'sonner';
import BadgeDisplay from '@/components/BadgeDisplay';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getLevel } from '@/utils/readingLevels';

const ChildProfileDetail = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [child, setChild] = useState(null);
  const [recentlyRead, setRecentlyRead] = useState([]);
  const [vocabulary, setVocabulary] = useState([]);
  const [badges, setBadges] = useState({ consecutive_days: 0, badges: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('recently-read');

  useEffect(() => {
    fetchChildData();
  }, [childId]);

  const fetchChildData = async () => {
    try {
      const [childRes, recentlyReadRes, vocabRes, badgesRes] = await Promise.all([
        axios.get(`${API}/children/${childId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/children/${childId}/recently-read`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/children/${childId}/vocabulary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/children/${childId}/badges`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setChild(childRes.data);
      setRecentlyRead(recentlyReadRes.data);
      setVocabulary(vocabRes.data.vocabulary);
      setBadges(badgesRes.data);
    } catch (error) {
      console.error('Failed to fetch child data:', error);
      toast.error('Failed to load child profile');
    } finally {
      setLoading(false);
    }
  };

  const speakWord = (word) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.rate = 0.8;
      utterance.pitch = 1.0;
      window.speechSynthesis.speak(utterance);
    } else {
      toast.error('Text-to-speech not supported in this browser');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="font-sketch" style={{ color: 'var(--text-secondary)' }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <p className="font-sketch text-lg" style={{ color: 'var(--text-primary)' }}>Child not found</p>
          <Button onClick={() => navigate('/children')} className="mt-4 font-sketch">Back to Children</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen page-enter" style={{ background: 'var(--bg-primary)' }}>
      <div className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/children')}
            variant="outline"
            className="rounded-full w-20"
            style={{
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              borderColor: 'var(--sketch-border)'
            }}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold font-heading" style={{ color: 'var(--text-primary)' }}>
            {child.name}'s Learning History
          </h1>
        </div>

        {/* Profile Card */}
        <div className="sketch-border p-6 rounded-xl mb-8" style={{ background: 'var(--bg-secondary)' }}>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            {child.profile_image && (
              <img
                src={`data:image/png;base64,${child.profile_image}`}
                alt={child.name}
                className="w-32 h-32 md:w-48 md:h-48 lg:w-64 lg:h-64 rounded-full object-cover sketch-border"
              />
            )}
            <div className="flex-1 text-center">
              <h2 className="text-2xl font-bold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>{child.name}</h2>
              <p className="font-sketch mb-4 capitalize" style={{ color: 'var(--text-secondary)' }}>Reading Level: {getLevel(child.reading_level)}</p>
              
              <div className="flex justify-center mb-4">
                <Button
                  onClick={() => navigate('/children')}
                  variant="outline"
                  size="sm"
                  className="font-sketch"
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--sketch-border)'
                  }}
                >
                  Manage Profile
                </Button>
              </div>
              
              {/* Consecutive Days Badge */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="flex items-center gap-2 sketch-border px-4 py-2 rounded-lg" style={{ background: 'var(--accent-3)' }}>
                  <Flame className="w-5 h-5 text-orange-500" />
                  <span className="font-sketch font-bold">{badges.consecutive_days} day streak!</span>
                </div>
              </div>
              
              {/* Badges */}
              <div className="flex flex-col items-center justify-center">
                <h3 className="text-sm font-sketch font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Achievements:</h3>
                <BadgeDisplay badges={badges.badges} consecutiveDays={badges.consecutive_days} />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('recently-read')}
            variant={activeTab === 'recently-read' ? 'default' : 'outline'}
            className="font-sketch"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Recently Read ({recentlyRead.length})
          </Button>
          <Button
            onClick={() => setActiveTab('vocabulary')}
            variant={activeTab === 'vocabulary' ? 'default' : 'outline'}
            className="font-sketch"
          >
            <Volume2 className="w-4 h-4 mr-2" />
            Vocabulary seen ({vocabulary.length})
          </Button>
        </div>

        {/* Recently Read Tab */}
        {activeTab === 'recently-read' && (
          <div className="space-y-4">
            {recentlyRead.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p className="font-sketch" style={{ color: 'var(--text-secondary)' }}>No stories read yet</p>
                </CardContent>
              </Card>
            ) : (
              recentlyRead.map((story) => (
                <Card
                  key={story.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  style={{ background: 'var(--bg-secondary)' }}
                  onClick={() => navigate(`/story/${story.id}`)}
                >
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">{story.title}</CardTitle>
                    <CardDescription className="font-sketch">
                      <span className="capitalize">{getLevel(story.reading_level)}</span> • {story.length} • {new Date(story.created_at).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  {story.summary && (
                    <CardContent>
                      <p className="font-sketch text-sm" style={{ color: 'var(--text-secondary)' }}>{story.summary}</p>
                    </CardContent>
                  )}
                </Card>
              ))
            )}
          </div>
        )}

        {/* Vocabulary Tab */}
        {activeTab === 'vocabulary' && (
          <div className="space-y-2">
            {vocabulary.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <Volume2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p className="font-sketch" style={{ color: 'var(--text-secondary)' }}>No vocabulary words yet</p>
                  <p className="font-sketch text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>Generate stories to build vocabulary!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vocabulary.map((item, idx) => (
                  <div
                    key={idx}
                    className="sketch-border p-4 rounded-lg hover:shadow-md transition-shadow"
                    style={{ background: 'var(--bg-secondary)', cursor: 'pointer' }}
                    onClick={() => speakWord(item.word)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h4 className="font-heading text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                          {item.word}
                        </h4>
                        <p className="font-sketch text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                          {item.definition}
                        </p>
                      </div>
                      <Button
                        onClick={(e) => { e.stopPropagation(); speakWord(item.word); }}
                        variant="ghost"
                        size="icon"
                        className="flex-shrink-0"
                        title="Listen"
                      >
                        <Volume2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChildProfileDetail;
